import { Typography } from "@mui/material"
import { format } from "date-fns"
import { de, enGB } from "date-fns/locale"
import { usePathname } from "next/navigation"
import { FC, useEffect } from "react"

import { mergeBookingDetail } from "../../states/bookingDetail"
import { SubHeader } from "src/components/services/account/overview/SubHeader"
import { create } from "src/helpers/bem"

import { getPath } from "src/helpers/paths"
import { useAccount } from "src/states/account"
import { useLocale } from "src/states/locale"
import { SharedPageProps } from "src/types/SharedPageProps"

import styles from "./DefaultPage.module.scss"

const bem = create(styles, "DefaultPage")

export interface DefaultPageProps extends SharedPageProps {
  additionals?: JSX.Element
}

export const DefaultPage: FC<DefaultPageProps> = ({
  additionals,
  page,
  children,
  messages,
}) => {
  const pathname = usePathname()
  const locale = useLocale()
  const account = useAccount()
  const profileTranslations = messages.pages.account.overview.profile

  const hasName = account.firstName && account.lastName

  const isHome = pathname === getPath(locale, "home")

  const today = new Date()
  const dateLocale = locale === "de" ? de : enGB
  const weekDayString = format(today, "EEEE", { locale: dateLocale })
  const dateString = format(today, "P", { locale: dateLocale })

  const renderHomeHeader = () => {
    return (
      <Typography variant="h3">
        {hasName
          ? `${profileTranslations.welcome}, ${account.firstName} ${account.lastName}!`
          : `${weekDayString}, ${dateString}`}
      </Typography>
    )
  }

  useEffect(() => {
    if (isHome) {
      // Normally, this should be executed when clicking on the button to create a new booking.
      // However, since this component is not in the code base (imported via wordpress), it is done here using an UseEffect.
      mergeBookingDetail({ editDraft: false })
    }
  }, [isHome])

  return (
    <>
      <div className={bem("Header")}>
        <div className={bem("Header__Breadcrumb")}>
          <SubHeader
            additionals={isHome ? renderHomeHeader() : additionals}
            homeButton={!isHome}
            lastBreadcrumbTitle={page?.data?.title ?? undefined}
          />
        </div>
      </div>
      <div className={bem("Content")}>{children}</div>
    </>
  )
}
